import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { staffAPI } from '../services/api';
import { Search, XCircle, MinusCircle, Clock } from 'lucide-react';

const AttendanceReport = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Default to current week
  const formatLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const now = new Date();
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  const friday = new Date(monday);
  friday.setDate(monday.getDate() + 4);

  const [startDate, setStartDate] = useState(formatLocal(monday));
  const [endDate, setEndDate] = useState(formatLocal(friday));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [breakDrafts, setBreakDrafts] = useState({}); // attendanceId -> string
  const [savingBreakId, setSavingBreakId] = useState(null);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const result = await staffAPI.getRangeAttendance(startDate, endDate);
      setData(result);
      setBreakDrafts({});
    } catch (err) {
      setError('Failed to load attendance report');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  const saveBreak = async (record) => {
    if (!record.attendanceId) return;
    const draft = breakDrafts[record.attendanceId];
    if (draft === undefined) return;

    let parsed;
    if (draft.trim() === '') {
      parsed = null;
    } else {
      const n = Number(draft);
      if (!Number.isFinite(n) || n < 0 || n > 600) {
        setError('Break minutes must be between 0 and 600');
        return;
      }
      parsed = n;
    }

    const current = record.breakMinutes === null || record.breakMinutes === undefined
      ? null
      : Number(record.breakMinutes);
    if (current === parsed) {
      setBreakDrafts(prev => {
        const next = { ...prev };
        delete next[record.attendanceId];
        return next;
      });
      return;
    }

    try {
      setSavingBreakId(record.attendanceId);
      setError('');
      await staffAPI.updateAttendance(record.attendanceId, { breakMinutes: parsed });
      await fetchReport();
    } catch (err) {
      setError('Failed to save break minutes');
    } finally {
      setSavingBreakId(null);
    }
  };

  const formatDuration = (totalMinutes) => {
    const mins = Math.round(totalMinutes);
    if (mins < 60) return `${mins}m`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const getStatusCell = (record) => {
    const cellStyle = { padding: '6px 8px', textAlign: 'center', fontSize: '12px', borderRight: '1px solid #e5e7eb', verticalAlign: 'top' };

    switch (record.status) {
      case 'not_scheduled':
        return (
          <td style={{ ...cellStyle, backgroundColor: '#f9fafb', color: '#9ca3af' }}>
            <MinusCircle size={14} style={{ display: 'block', margin: '0 auto 2px' }} />
            Off
          </td>
        );
      case 'missed':
        return (
          <td style={{ ...cellStyle, backgroundColor: '#fef2f2', color: '#dc2626' }}>
            <XCircle size={14} style={{ display: 'block', margin: '0 auto 2px' }} />
            Missed
          </td>
        );
      case 'checked_in':
        return (
          <td style={{ ...cellStyle, backgroundColor: record.isLate ? '#fffbeb' : '#eff6ff' }}>
            <div style={{ fontWeight: '600', color: record.isLate ? '#d97706' : '#2563eb' }}>
              {formatTime(record.checkIn)}
            </div>
            {record.isLate && (
              <div style={{ color: '#d97706', fontSize: '11px' }}>+{formatDuration(record.lateMinutes)}</div>
            )}
            <div style={{ color: '#9ca3af', fontSize: '11px' }}>Still in</div>
          </td>
        );
      case 'checked_out': {
        const draft = breakDrafts[record.attendanceId];
        const inputValue = draft !== undefined
          ? draft
          : (record.breakMinutes === null || record.breakMinutes === undefined ? '' : String(record.breakMinutes));
        const isOverride = record.breakMinutes !== null && record.breakMinutes !== undefined;
        return (
          <td style={{
            ...cellStyle,
            backgroundColor: (record.isLate || record.isEarlyLeave) ? '#fffbeb' : '#f0fdf4'
          }}>
            <div style={{ fontWeight: '600', color: record.isLate ? '#d97706' : '#16a34a', fontSize: '11px' }}>
              {formatTime(record.checkIn)}
              {record.isLate && <span style={{ color: '#d97706' }}> (+{formatDuration(record.lateMinutes)})</span>}
            </div>
            <div style={{ fontWeight: '600', color: record.isEarlyLeave ? '#d97706' : '#16a34a', fontSize: '11px' }}>
              {formatTime(record.checkOut)}
              {record.isEarlyLeave && <span style={{ color: '#d97706' }}> (-{formatDuration(record.earlyLeaveMinutes)})</span>}
            </div>
            <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px' }}>
              <input
                type="number"
                min="0"
                max="600"
                step="5"
                inputMode="numeric"
                disabled={!isAdmin || savingBreakId === record.attendanceId}
                value={inputValue}
                placeholder={`${record.appliedBreakMinutes}`}
                onChange={e => setBreakDrafts(prev => ({ ...prev, [record.attendanceId]: e.target.value }))}
                onBlur={() => saveBreak(record)}
                onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                title={isOverride ? 'Break (manual)' : `Break (auto: ${record.appliedBreakMinutes}m)`}
                style={{
                  width: '42px', padding: '2px 4px', fontSize: '11px', textAlign: 'center',
                  border: '1px solid #d1d5db', borderRadius: '4px',
                  backgroundColor: isAdmin ? 'white' : '#f9fafb',
                  color: isOverride ? '#111827' : '#6b7280',
                  fontStyle: isOverride ? 'normal' : 'italic'
                }}
              />
              <span style={{ fontSize: '10px', color: '#6b7280' }}>m brk</span>
            </div>
            <div style={{ marginTop: '2px', fontSize: '11px', fontWeight: '600', color: '#065f46' }}>
              {formatDuration(record.netWorkedMinutes)} net
            </div>
          </td>
        );
      }
      case 'pending':
        return (
          <td style={{ ...cellStyle, backgroundColor: '#fffbeb', color: '#92400e' }}>
            <Clock size={14} style={{ display: 'block', margin: '0 auto 2px' }} />
            Pending
          </td>
        );
      case 'upcoming':
        return (
          <td style={{ ...cellStyle, backgroundColor: '#f9fafb', color: '#d1d5db' }}>
            -
          </td>
        );
      default:
        return <td style={cellStyle}>-</td>;
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Header */}
      <div style={{ backgroundColor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
            <h1 style={{ margin: 0, fontSize: '24px', color: '#10b981' }}>KFRESH</h1>
            <nav style={{ display: 'flex', gap: '20px' }}>
              <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', fontSize: '15px', color: '#374151', cursor: 'pointer', fontWeight: '500' }}>Dashboard</button>
              <button onClick={() => navigate('/pallets')} style={{ background: 'none', border: 'none', fontSize: '15px', color: '#374151', cursor: 'pointer', fontWeight: '500' }}>Pallet Sheets</button>
              <button onClick={() => navigate('/reports')} style={{ background: 'none', border: 'none', fontSize: '15px', color: '#374151', cursor: 'pointer', fontWeight: '500' }}>Reports</button>
              {user?.role === 'admin' && (
                <button onClick={() => navigate('/admin')} style={{ background: 'none', border: 'none', fontSize: '15px', color: '#10b981', cursor: 'pointer', fontWeight: '600' }}>Admin</button>
              )}
            </nav>
          </div>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: '600', fontSize: '14px' }}>{user?.firstName} {user?.lastName}</div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>{user?.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
            </div>
            <button onClick={() => { logout(); navigate('/login'); }} style={{ padding: '8px 16px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>Logout</button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 20px 40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 style={{ margin: '0 0 4px 0', fontSize: '28px', color: '#111827' }}>Attendance Report</h2>
            <p style={{ color: '#6b7280', fontSize: '15px', margin: 0 }}>View attendance trends, missed days, and punctuality</p>
          </div>
          <button
            onClick={() => navigate('/attendance')}
            style={{
              padding: '10px 20px', backgroundColor: '#f0f9ff', color: '#0369a1',
              border: '1px solid #bae6fd', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500'
            }}
          >
            Daily View
          </button>
        </div>

        {/* Filters */}
        <div style={{
          display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '24px',
          backgroundColor: 'white', padding: '16px 20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          flexWrap: 'wrap'
        }}>
          <div>
            <label style={{ fontSize: '13px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>From</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              style={{ padding: '8px 12px', border: '2px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
          </div>
          <div>
            <label style={{ fontSize: '13px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>To</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              style={{ padding: '8px 12px', border: '2px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }} />
          </div>
          <div style={{ alignSelf: 'flex-end' }}>
            <button onClick={fetchReport} disabled={loading}
              style={{
                padding: '10px 24px', backgroundColor: '#10b981', color: 'white', border: 'none',
                borderRadius: '8px', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '500',
                display: 'flex', alignItems: 'center', gap: '8px', opacity: loading ? 0.7 : 1
              }}>
              <Search size={16} /> {loading ? 'Loading...' : 'Generate Report'}
            </button>
          </div>
        </div>

        {error && (
          <div style={{ padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', marginBottom: '16px' }}>
            {error}
          </div>
        )}

        {!data && !loading && (
          <div style={{ textAlign: 'center', padding: '60px', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <p style={{ color: '#6b7280', fontSize: '16px' }}>Select a date range and click "Generate Report" to view attendance data.</p>
          </div>
        )}

        {data && data.staff.map(staff => (
          <div key={staff.staffId} style={{
            backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            marginBottom: '20px', overflow: 'hidden'
          }}>
            {/* Staff header with summary */}
            <div style={{ padding: '16px 20px', borderBottom: '2px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '44px', height: '44px', borderRadius: '50%', backgroundColor: '#d1fae5',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '16px', fontWeight: '600', color: '#065f46'
                }}>
                  {staff.firstName[0]}{staff.lastName[0]}
                </div>
                <div>
                  <div style={{ fontWeight: '600', fontSize: '18px', color: '#111827' }}>
                    {staff.firstName} {staff.lastName}
                  </div>
                  {staff.summary.avgCheckIn && (
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                      Avg: {staff.summary.avgCheckIn} - {staff.summary.avgCheckOut || 'N/A'}
                    </div>
                  )}
                </div>
              </div>

              {/* Summary badges */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <div style={{ padding: '6px 12px', borderRadius: '8px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', textAlign: 'center' }}>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#16a34a' }}>{staff.summary.totalDaysPresent}</div>
                  <div style={{ fontSize: '11px', color: '#6b7280' }}>Present</div>
                </div>
                {staff.summary.missedDays > 0 && (
                  <div style={{ padding: '6px 12px', borderRadius: '8px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: '#dc2626' }}>{staff.summary.missedDays}</div>
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>Missed</div>
                  </div>
                )}
                {staff.summary.lateDays > 0 && (
                  <div style={{ padding: '6px 12px', borderRadius: '8px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: '#d97706' }}>{staff.summary.lateDays}</div>
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>Late</div>
                  </div>
                )}
                {staff.summary.earlyFinishDays > 0 && (
                  <div style={{ padding: '6px 12px', borderRadius: '8px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: '#d97706' }}>{staff.summary.earlyFinishDays}</div>
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>Early Out</div>
                  </div>
                )}
                <div style={{ padding: '6px 12px', borderRadius: '8px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', textAlign: 'center' }}>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: '#2563eb' }}>{staff.summary.totalWorkedHours}h</div>
                  <div style={{ fontSize: '11px', color: '#6b7280' }}>Worked (net)</div>
                </div>
              </div>
            </div>

            {/* Daily records table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    {staff.dailyRecords.map(record => (
                      <th key={record.date} style={{
                        padding: '8px 6px', fontWeight: '500', color: '#6b7280', fontSize: '12px',
                        borderBottom: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb',
                        minWidth: '70px'
                      }}>
                        {formatDate(record.date)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {staff.dailyRecords.map(record => (
                      <React.Fragment key={record.date}>
                        {getStatusCell(record)}
                      </React.Fragment>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ))}

        {/* Legend */}
        {data && (
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', padding: '16px 20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginTop: '12px'
          }}>
            <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Legend</div>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', fontSize: '13px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '16px', height: '16px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '3px' }} />
                <span style={{ color: '#374151' }}>On Time</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '16px', height: '16px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '3px' }} />
                <span style={{ color: '#374151' }}>Late / Early Out (15+ min)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '16px', height: '16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '3px' }} />
                <span style={{ color: '#374151' }}>Missed</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '16px', height: '16px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '3px' }} />
                <span style={{ color: '#374151' }}>Not Scheduled</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '16px', height: '16px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '3px' }} />
                <span style={{ color: '#374151' }}>Still Checked In</span>
              </div>
            </div>
            <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed #e5e7eb', fontSize: '12px', color: '#6b7280' }}>
              Break rule: any day worked over 5 hours auto-deducts a 30 min unpaid break. Admin can edit the per-day break value (in minutes); clear the field to revert to auto. Italic value = auto, normal value = manual override.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceReport;
